/**
 * Alerts widget: shows items requiring attention.
 * Displays pending payments, new registrations, etc.
 */

import { Link } from "react-router-dom";
import {
  IconClock,
  IconChevronRight,
} from "../../../components/Icons.tsx";

export interface AlertItem {
  id: string;
  type: "warning" | "info";
  title: string;
  count: number;
  path: string;
}

interface AlertsWidgetProps {
  alerts: AlertItem[];
}

function AlertRow({ alert }: { alert: AlertItem }) {
  const isWarning = alert.type === "warning";
  
  return (
    <Link
      to={alert.path}
      className={`flex items-center gap-3 py-3 px-4 rounded-xl transition-colors ${
        isWarning
          ? "bg-amber-50 border border-amber-200 active:bg-amber-100"
          : "bg-blue-50 border border-blue-200 active:bg-blue-100"
      }`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          isWarning ? "bg-amber-100 text-amber-600" : "bg-blue-100 text-blue-600"
        }`}
      >
        <IconClock className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`text-lg font-bold ${
              isWarning ? "text-amber-700" : "text-blue-700"
            }`}
          >
            {alert.count}
          </span>
          <span
            className={`text-[14px] font-medium ${
              isWarning ? "text-amber-800" : "text-blue-800"
            }`}
          >
            {alert.title}
          </span>
        </div>
      </div>
      <IconChevronRight
        className={`w-5 h-5 ${isWarning ? "text-amber-400" : "text-blue-400"}`}
      />
    </Link>
  );
}

export function AlertsWidget({ alerts }: AlertsWidgetProps) {
  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 mb-6">
      {alerts.map((alert) => (
        <AlertRow key={alert.id} alert={alert} />
      ))}
    </div>
  );
}
